/**
 * An event logger that sends information to the Premier Systems Ltd. event collector.
 *
 * You must set up a new application to receive events on the server as the application domain needs to be whitelisted
 * for CORS purposes.
 *
 * This logger will augment the event packets with useful device/browser information.
 */

/**
 * Properties with special meaning to the event service.
 */
export interface EventProperties {
  timestamp?: number; // millis since epoch
  severity?: string; // INFO, ERROR
  eventName?: string;
  deviceId?: string;

  // These are set by the server and should in most cases not be set by the client
  forwardedFor?: string;
  forwardedForProto?: string;

  [key: string]: any;
}

export type EventPropertyDecorator = (toDecorate: EventProperties) => void;

export interface EventLogger {
  logEvent(eventName: string, event: EventProperties): void;
}

export class ConsoleLogger implements EventLogger {
  logEvent(eventName: string, event: EventProperties = {}): void {
    // eslint-disable-next-line no-console
    console.log("event log", eventName, event);
  }
}

export class NoopLogger implements EventLogger {
  logEvent(eventName: string, event: EventProperties = {}): void {}
}

export class EventCollectorEventLogger implements EventLogger {
  private readonly maxQueueSize: number;
  private readonly eventDecorators: EventPropertyDecorator[];

  public queue: EventProperties[] = [];

  /**
   * Creates an event collector.
   *
   * @param eventDecorators Decorators that will add properties to each and every event submitted.
   * @param maxQueueSize Maximum number of items in the queue before events will be lost.
   */
  public constructor(
    eventDecorators: EventPropertyDecorator[] = [],
    maxQueueSize: number = 500
  ) {
    this.maxQueueSize = maxQueueSize;
    this.eventDecorators = eventDecorators;
  }

  /**
   * Log an event to the server (well actually queue it for transmission).
   */
  public logEvent(
    eventName: string,
    eventProperties: EventProperties = {}
  ): void {
    if (this.queue.length > this.maxQueueSize) {
      this.queue.push({ error: "Client event limit reached" });
      return;
    }
    eventProperties.eventName = eventName;

    this.eventDecorators.forEach((eventDecorator) =>
      eventDecorator(eventProperties)
    );

    // Fire and forget
    this.queue.push(eventProperties);
  }

  public addDecorator(decorator: EventPropertyDecorator) {
    this.eventDecorators.push(decorator);
  }
}

export const commonBrowserPropertiesDecorator: EventPropertyDecorator = (
  toDecorate
) => {
  toDecorate.userAgent = navigator.userAgent;
  toDecorate.screen = window.screen;
};

export const captureTimeBrowserPropertyDecorator: EventPropertyDecorator = (
  toDecorate
) => {
  toDecorate.clientDimensions = {
    clientWidth: document.documentElement.clientWidth,
    clientHeight: document.documentElement.clientHeight,
  };
};

export const commonNavigationPropertiesDecorator: EventPropertyDecorator = (
  toDecorate
) => {
  toDecorate.pathName = window.location.pathname;
  toDecorate.host = window.location.host;
};
